<template>
  <v-dialog
    :model-value="modelValue"
    :max-width="isBvReport ? 1100 : 900"
    scrollable
    @update:model-value="emit('update:modelValue', $event)">
    <v-card v-if="call">
      <v-card-title class="voice-ai-dialog__title">
        Call to {{ call.toPhoneNumber }}
        <v-chip size="small" class="ml-2" :color="statusColor(call.status)">{{ statusLabel(call.status) }}</v-chip>
        <v-chip v-if="call.outcome" size="small" class="ml-2" variant="outlined">{{
          outcomeLabel(call.outcome)
        }}</v-chip>
        <v-chip
          v-if="isAccuracySuspect(call)"
          size="small"
          class="ml-2"
          color="amber-darken-2"
          variant="tonal"
          prepend-icon="mdi-alert-circle-outline">
          Follow-up suggested
        </v-chip>
      </v-card-title>
      <v-divider />
      <v-card-text v-if="isBvReport" class="voice-ai-dialog__body voice-ai-dialog__body--bv">
        <BVVerificationReport
          :call="call"
          :transcript-segments="transcriptSegments"
          class="voice-ai-dialog__bv-left"
          @jump="jumpToSegment"
          @re-verify="emit('call-again', call)" />
        <div class="voice-ai-dialog__bv-transcript">
          <div v-if="recordingUrl" class="voice-ai-dialog__audio"><audio controls :src="recordingUrl" /></div>
          <div class="voice-ai-dialog__transcript">
            <div v-if="loadingTranscript" class="voice-ai-dialog__empty">Loading transcript…</div>
            <div v-else-if="transcriptError" class="voice-ai-dialog__empty">{{ transcriptError }}</div>
            <div v-else-if="!transcriptSegments.length" class="voice-ai-dialog__empty">No transcript available.</div>
            <div
              v-for="(segment, idx) in transcriptSegments"
              :key="idx"
              :ref="(el) => (segmentEls[idx] = el)"
              :class="[
                'voice-ai-dialog__seg',
                `voice-ai-dialog__seg--${speakerClass(segment)}`,
                { 'voice-ai-dialog__seg--flash': flashIndex === idx },
              ]">
              <div class="voice-ai-dialog__seg-speaker">{{ speakerLabel(segment) }}</div>
              <div class="voice-ai-dialog__seg-text">{{ segment.message || segment.text }}</div>
            </div>
          </div>
        </div>
      </v-card-text>
      <v-card-text v-else class="voice-ai-dialog__body">
        <div class="voice-ai-dialog__left">
          <div v-if="recordingUrl" class="voice-ai-dialog__audio">
            <audio controls :src="recordingUrl" />
          </div>
          <div class="voice-ai-dialog__transcript">
            <div v-if="loadingTranscript" class="voice-ai-dialog__empty">Loading transcript…</div>
            <div v-else-if="transcriptError" class="voice-ai-dialog__empty">{{ transcriptError }}</div>
            <div v-else-if="!transcriptSegments.length" class="voice-ai-dialog__empty">No transcript available.</div>
            <div
              v-for="(segment, idx) in transcriptSegments"
              :key="idx"
              :class="['voice-ai-dialog__seg', `voice-ai-dialog__seg--${speakerClass(segment)}`]">
              <div class="voice-ai-dialog__seg-speaker">{{ speakerLabel(segment) }}</div>
              <div class="voice-ai-dialog__seg-text">{{ segment.message || segment.text }}</div>
            </div>
          </div>
        </div>
        <aside class="voice-ai-dialog__right">
          <div class="voice-ai-dialog__panel-section">
            <div class="voice-ai-dialog__panel-label">Outcome</div>
            <div class="voice-ai-dialog__panel-value">{{ outcomeLabel(call.outcome) || "—" }}</div>
          </div>
          <div v-if="call.outcomeDetails?.repName" class="voice-ai-dialog__panel-section">
            <div class="voice-ai-dialog__panel-label">Spoke with</div>
            <div class="voice-ai-dialog__panel-value">
              {{ call.outcomeDetails.repName
              }}<span v-if="call.outcomeDetails.repTitle"> ({{ call.outcomeDetails.repTitle }})</span>
            </div>
          </div>
          <div v-if="call.outcomeDetails?.faxNumber" class="voice-ai-dialog__panel-section">
            <div class="voice-ai-dialog__panel-label">Fax number</div>
            <div class="voice-ai-dialog__panel-value voice-ai-dialog__mono">{{ call.outcomeDetails.faxNumber }}</div>
          </div>
          <div v-if="call.outcomeDetails?.callbackTime" class="voice-ai-dialog__panel-section">
            <div class="voice-ai-dialog__panel-label">Callback time</div>
            <div class="voice-ai-dialog__panel-value">{{ call.outcomeDetails.callbackTime }}</div>
          </div>
          <div v-if="call.outcomeDetails?.notes" class="voice-ai-dialog__panel-section">
            <div class="voice-ai-dialog__panel-label">AI notes</div>
            <div class="voice-ai-dialog__panel-value">{{ call.outcomeDetails.notes }}</div>
          </div>
          <div v-for="item in collectedItems" :key="item.label" class="voice-ai-dialog__panel-section">
            <div class="voice-ai-dialog__panel-label">{{ item.label }}</div>
            <pre v-if="item.pre" class="voice-ai-dialog__panel-value voice-ai-dialog__mono voice-ai-dialog__pre">{{
              item.value
            }}</pre>
            <div v-else class="voice-ai-dialog__panel-value">{{ item.value }}</div>
          </div>
          <div class="voice-ai-dialog__panel-section">
            <div class="voice-ai-dialog__panel-label">Started</div>
            <div class="voice-ai-dialog__panel-value">{{ formatDate(call.createdOn) }}</div>
          </div>
          <div v-if="TERMINAL_STATUSES.includes(call.status)" class="voice-ai-dialog__panel-section">
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              block
              prepend-icon="mdi-phone-refresh"
              @click="emit('call-again', call)">
              Call Again
            </v-btn>
          </div>
        </aside>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn variant="text" @click="emit('update:modelValue', false)">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";

import BVVerificationReport from "@/components/voice_ai/BVVerificationReport.vue";
import {
  statusColor,
  statusLabel,
  outcomeLabel,
  TERMINAL_STATUSES,
  isAccuracySuspect,
} from "@/components/voice_ai/constants";
import { voiceAiService } from "@/services/voiceAiService";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  call: { type: Object, default: null },
});
const emit = defineEmits(["update:modelValue", "call-again"]);

const transcriptSegments = ref([]);
const loadingTranscript = ref(false);
const transcriptError = ref("");
const recordingUrl = ref("");

// BV calls with a computed verification get the report layout instead of the generic panel.
const isBvReport = computed(
  () => props.call?.templateKey === "BENEFITS_VERIFICATION" && !!props.call?.metadata?.verification
);
const segmentEls = ref({});
const flashIndex = ref(null);

function jumpToSegment(idx) {
  const el = segmentEls.value[idx];
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  flashIndex.value = idx;
  setTimeout(() => {
    if (flashIndex.value === idx) flashIndex.value = null;
  }, 2200);
}

// Keys already rendered by the dedicated sections above.
const RENDERED_DETAIL_KEYS = ["outcome", "repName", "repTitle", "faxNumber", "callbackTime", "notes"];

function humanizeKey(key) {
  const words = key.replace(/([A-Z])/g, " $1").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// Everything else the AI extracted (template-specific structured data — collected clinical info,
// identity verification, BV coverage fields, …), rendered generically so new templates need no UI work.
const collectedItems = computed(() => {
  const details = props.call?.outcomeDetails;
  if (!details) return [];
  return Object.entries(details)
    .filter(([key, value]) => !RENDERED_DETAIL_KEYS.includes(key) && value !== "" && value != null)
    .map(([key, value]) => {
      if (typeof value === "boolean") return { label: humanizeKey(key), value: value ? "Yes" : "No", pre: false };
      if (typeof value === "object")
        return { label: humanizeKey(key), value: JSON.stringify(value, null, 2), pre: true };
      return { label: humanizeKey(key), value: String(value), pre: false };
    });
});

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

function speakerClass(seg) {
  const role = (seg.role || seg.speaker?.id || "").toLowerCase();
  if (role === "bot" || role.includes("assistant") || role.includes("speaker_3")) return "agent";
  return "rep";
}

function speakerLabel(seg) {
  if (seg.role === "assistant" || seg.role === "bot") return "Agent";
  if (seg.role === "user") return "Rep";
  return seg.speaker?.name || seg.speakerName || "—";
}

// Vapi's artifact.messages[] includes the system prompt (role "system") and any tool
// calls as the leading entries — only conversational turns belong in the transcript view.
function isConversationTurn(seg) {
  const role = (seg.role || seg.speaker?.id || "").toLowerCase();
  if (role.includes("system") || role.includes("tool")) return false;
  // A turn must carry some spoken text.
  return Boolean(seg.message || seg.text || seg.content || seg.transcript);
}

function applyTranscriptData(data) {
  const raw = Array.isArray(data) ? data : data?.segments || [];
  transcriptSegments.value = raw.filter(isConversationTurn);
}

watch(
  // status is in the key so a call completing while the dialog is open (live polling swaps in the
  // fresh row) refetches media — the transcript and recording appear the moment they're archived.
  () => [props.modelValue, props.call?.id, props.call?.status],
  async ([open]) => {
    transcriptSegments.value = [];
    recordingUrl.value = "";
    transcriptError.value = "";
    const call = props.call;
    if (!open || !call) return;

    // Mint fresh presigned URLs on demand (they expire ~50min, so are not stored).
    if (call.hasRecording) {
      try {
        const resp = await voiceAiService.getCallMedia(call.id, "recording");
        recordingUrl.value = resp?.url || "";
      } catch {
        // Non-fatal: audio just won't render.
      }
    }
    if (!call.hasTranscript) return;
    loadingTranscript.value = true;
    try {
      const resp = await voiceAiService.getCallMedia(call.id, "transcript");
      if (!resp?.url) throw new Error("no transcript url");
      const response = await fetch(resp.url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      // Vapi sends an array of {role, message, ...} OR a Deepgram-style {segments: [...]}
      applyTranscriptData(data);
    } catch (err) {
      transcriptError.value = `Failed to load transcript: ${err.message}`;
    } finally {
      loadingTranscript.value = false;
    }
  }
);
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.voice-ai-dialog__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  white-space: normal;
  font-size: 16px;
  font-weight: 600;
  color: $lh_color_navy_dark;
}
.voice-ai-dialog__body {
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 16px;
  max-height: 70vh;
}
.voice-ai-dialog__body--bv {
  grid-template-columns: minmax(0, 1.35fr) minmax(280px, 1fr);
  max-height: 78vh;
}
.voice-ai-dialog__bv-left {
  overflow-y: auto;
  padding-right: 4px;
}
.voice-ai-dialog__bv-transcript {
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
  border-left: 1px solid rgba(5, 46, 94, 0.06);
  padding-left: 22px;
}
.voice-ai-dialog__body--bv .voice-ai-dialog__seg-text {
  font-family: inherit;
  font-size: 13px;
  color: rgba(4, 33, 67, 0.82);
}
.voice-ai-dialog__body--bv .voice-ai-dialog__seg-speaker {
  color: rgba(94, 120, 152, 0.75);
}
.voice-ai-dialog__seg--flash {
  animation: bv-flash 2.2s ease-out;
  border-radius: 6px;
}
@keyframes bv-flash {
  0% {
    background: rgba(36, 112, 202, 0.18);
  }
  100% {
    background: transparent;
  }
}
.voice-ai-dialog__left {
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
}
.voice-ai-dialog__audio {
  audio {
    width: 100%;
  }
}
.voice-ai-dialog__transcript {
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
}
.voice-ai-dialog__seg {
  margin-bottom: 12px;
  &--agent {
    color: $lh_sidebar_accent;
  }
}
.voice-ai-dialog__seg-speaker {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: $lh_color_blue_gray;
  margin-bottom: 2px;
}
.voice-ai-dialog__seg-text {
  font-family: "JetBrains Mono", monospace;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
}
.voice-ai-dialog__right {
  border-left: 1px solid $lh_dash_border;
  padding-left: 16px;
}
.voice-ai-dialog__panel-section {
  margin-bottom: 12px;
}
.voice-ai-dialog__panel-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: $lh_color_blue_gray;
  margin-bottom: 2px;
}
.voice-ai-dialog__panel-value {
  font-size: 13px;
  color: $lh_color_navy_dark;
}
.voice-ai-dialog__mono {
  font-family: "JetBrains Mono", monospace;
}
.voice-ai-dialog__pre {
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-word;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  padding: 6px;
  margin: 0;
}
.voice-ai-dialog__empty {
  font-size: 13px;
  color: $lh_color_blue_gray;
  opacity: 0.7;
  padding: 12px 0;
}
</style>
